import React, { useContext, useEffect, useState } from 'react';
import {
  InputField,
  SelectField,
  CheckboxField
} from '@trp-beacon-ui-kit/react-controls';
import styles from './FONASUMinorInformation.css';
import SectionHeading from '../Typography/SectionHeading/SectionHeading';
import EditButton from '../Typography/EditButton/EditButton';

import { states } from '../FONASUCustomerInformation/util';
import { FlowContext } from '../../core/flow-engine';
import {
  formatSsnForDisplay,
  handleInputFormat
} from '../../flows/shared/utils';
import {
  stateValidation,
  addressLine1Validation,
  poBoxLine1Validation,
  addressLine2Validation,
  poBoxLine2Validation,
  cityValidation,
  zipCodeValidation,
  restrictedFirstNameValidation,
  restrictedMiddleNameValidation,
  restrictedLastNameValidation
} from '../../flows/shared/validations';
import {
  customerSummaryLine1,
  customerSummaryLine3
} from '../ReviewCombinedPersonalInformation/util';
import { withDefault } from '../../core/util';

export const MinorInfo = ({ additionalMinorCustomer }) => {
  const {
    formProps: {
      minorFirstName,
      minorMiddleName,
      minorLastName,
      minorDob,
      minorSsn,
      minorCitizenship,
      minorSameAddress,
      minorResidentialStreet1,
      minorResidentialStreet2,
      minorResidentialCity,
      minorResidentialState,
      minorResidentialZip,
      minorGoverningState
    },
    appState: {
      store: {
        app: {
          customer: {
            residentialAddress: { state }
          },
          governingState
        }
      }
    }
  } = useContext(FlowContext);

  const [sameAddressChecked, setSameAddressChecked] = useState(
    additionalMinorCustomer ? additionalMinorCustomer.sameAddress : true
  );
  const [enableStateGovernance, setEnableStateGovernance] = useState(
    !!governingState
  );
  const [minorState, setMinorState] = useState(
    additionalMinorCustomer
      ? additionalMinorCustomer?.residentialAddress?.state
      : null
  );

  const [dobValue, setDobValue] = useState('');
  const [ssnValue, setSSNValue] = useState('');

  const toggleSameAddressChecked = () => {
    setSameAddressChecked(!sameAddressChecked);
  };

  const verifyStateGoverningLogic = ({ target: { value } }) => {
    if (state !== value) {
      setEnableStateGovernance(true);
      setMinorState(value);
    } else {
      setEnableStateGovernance(false);
      setMinorState(null);
    }
  };

  return (
    <>
      <div data-name="minorInfoWrapper">
        <div className="review-section">
          <div className="personal-info">
            <div className="qv-row">
              <div className="qv-col">
                <InputField
                  type="text"
                  title="First Name"
                  {...minorFirstName}
                  defaultValue={additionalMinorCustomer?.name?.firstName}
                  maxLength="35"
                  validation={restrictedFirstNameValidation}
                />
              </div>
              <div className="qv-col">
                <InputField
                  type="text"
                  title="Middle Name"
                  {...minorMiddleName}
                  defaultValue={additionalMinorCustomer?.name?.middleName}
                  validation={restrictedMiddleNameValidation}
                />
              </div>
              <div className="qv-col">
                <InputField
                  type="text"
                  title="Last Name"
                  {...minorLastName}
                  defaultValue={additionalMinorCustomer?.name?.lastName}
                  maxLength="35"
                  validation={restrictedLastNameValidation}
                />
              </div>
            </div>
            <div className="qv-row">
              <div className="qv-col">
                <InputField
                  type="text"
                  title="Date of Birth"
                  onChange={evt => handleInputFormat(evt, 'DATE', setDobValue)}
                  placeholder="mm/dd/yyyy"
                  maxLength="10"
                  {...minorDob}
                  value={withDefault(dobValue, additionalMinorCustomer?.dob)}
                />
              </div>
              <div className="qv-col">
                <InputField
                  title="Social Security Number"
                  placeholder="000-00-0000"
                  maxLength="11"
                  onChange={evt => handleInputFormat(evt, 'SSN', setSSNValue)}
                  {...minorSsn}
                  value={withDefault(
                    ssnValue,
                    formatSsnForDisplay(additionalMinorCustomer?.taxID)
                  )}
                />
              </div>
              <div className="qv-col">
                <SelectField
                  title="Citizenship"
                  {...minorCitizenship}
                  defaultValue={additionalMinorCustomer?.citizenship}>
                  <option value="US_CITIZEN">U.S. Citizen</option>
                  <option value="RESIDENT_ALIEN">U.S. Resident Alien</option>
                </SelectField>
              </div>
            </div>
            <CheckboxField
              name="minorSameAddress"
              data-name="minorSameAddress"
              title="Use same contact information for Minor"
              {...minorSameAddress}
              defaultChecked={sameAddressChecked}
              onClick={toggleSameAddressChecked}
            />
            {!sameAddressChecked && (
              <div className="address-wrapper">
                <div className="qv-row">
                  <div className="qv-col">
                    <InputField
                      type="text"
                      title="Address Line 1"
                      {...minorResidentialStreet1}
                      defaultValue={
                        additionalMinorCustomer?.residentialAddress?.street1
                      }
                      validation={[
                        addressLine1Validation,
                        poBoxLine1Validation
                      ]}
                      maxLength="35"
                    />
                  </div>
                  <div className="qv-col">
                    <InputField
                      type="text"
                      title="Address Line 2"
                      {...minorResidentialStreet2}
                      defaultValue={
                        additionalMinorCustomer?.residentialAddress?.street2
                      }
                      validation={[
                        addressLine2Validation,
                        poBoxLine2Validation
                      ]}
                      maxLength="35"
                    />
                  </div>
                </div>
                <div className="qv-row">
                  <div className="qv-col">
                    <InputField
                      type="text"
                      title="City"
                      {...minorResidentialCity}
                      defaultValue={
                        additionalMinorCustomer?.residentialAddress?.city
                      }
                      validation={cityValidation}
                    />
                  </div>
                  <div className="qv-col">
                    <InputField
                      type="text"
                      title="Zip Code"
                      {...minorResidentialZip}
                      defaultValue={
                        additionalMinorCustomer?.residentialAddress?.zip
                      }
                      validation={zipCodeValidation}
                    />
                  </div>
                  <div className="qv-col">
                    <SelectField
                      data-name="stateInput"
                      title="State"
                      {...minorResidentialState}
                      defaultValue={
                        additionalMinorCustomer?.residentialAddress?.state
                      }
                      validation={stateValidation}
                      onChange={verifyStateGoverningLogic}>
                      <option hidden value="">
                        State
                      </option>
                      {states.map(item => (
                        <option key={item} value={item}>
                          {item}
                        </option>
                      ))}
                    </SelectField>
                  </div>
                </div>
              </div>
            )}

            {enableStateGovernance && (
              <div className="qv-row">
                <div className="qv-col">
                  <SelectField
                    title="Governing state"
                    validation={stateValidation}
                    {...minorGoverningState}
                    defaultValue={governingState}>
                    <option hidden value="">
                      Select
                    </option>
                    <option value={state}>
                      {state} (my state of residence)
                    </option>
                    <option value={minorState}>
                      {minorState} (minor state of residence)
                    </option>
                  </SelectField>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      <style jsx>{styles}</style>
    </>
  );
};

export const shouldValidate = (additionalCustomers, editMinorInfo) =>
  additionalCustomers?.length > 0 || editMinorInfo;

const FONASUMinorInformation = ({
  showSuccess,
  editMinorInfo,
  setEditMinorInfo
}) => {
  const {
    next,
    appState: {
      store: {
        additionalCustomers,
        loadedData: { minorEditMode }
      }
    }
  } = useContext(FlowContext);

  const isValid = shouldValidate(additionalCustomers, editMinorInfo);

  useEffect(() => {
    setEditMinorInfo(minorEditMode);
  }, [minorEditMode]);

  const handleClick = async () => {
    if (editMinorInfo) {
      await next({
        section: 'minorCustomer'
      })();
    } else {
      await next('unlockMinorSection')();
    }
  };

  return (
    <>
      <div className="section-header">
        <SectionHeading text="Minor">
          <EditButton
            onClick={handleClick}
            currentlyEditing={editMinorInfo}
            disabled={!isValid || showSuccess}
          />
        </SectionHeading>
      </div>
      {editMinorInfo ? (
        <MinorInfo additionalMinorCustomer={additionalCustomers?.[0]} />
      ) : (
        <div className="review-customer-information">
          {additionalCustomers?.length > 0 ? (
            <div className="customer-information-lines">
              <div>{customerSummaryLine1(additionalCustomers[0])}</div>
              <div>{customerSummaryLine3(additionalCustomers[0])}</div>
            </div>
          ) : (
            <div>You have not added minor details yet.</div>
          )}
        </div>
      )}
      <style jsx>{styles}</style>
    </>
  );
};

export default FONASUMinorInformation;
